import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { ToggleSwitch } from '../../../ToggleSwitch/ToggleSwitch';
import RadioButton from '../../../Form/RadioButton/RadioButton';
import MainButton from '../../../buttons/MainButton';
import TextButton from '../../../buttons/TextButton/TextButton';
import { MailboxIcon } from '../../../Icons';
import { DeletePopUp } from '../../../ModalWindows/JobAlertModal/JobAlertModalNew';
import BaseModal from '../../../ModalWindows/BaseModal';
import WithCloseIcon from '../../../ComposableWrappers/WithCloseIcon/WithCloseIcon';
import { editJobAlert, deleteJobAlert, revertDeletedJobAlert, updateCardStyle } from '../../../../../actions/jobAlerts';
import { removeNotification, setNotification } from '../../../../../actions/appNotifications';
import { appNotificationsTypes, responsiveSizeTimesheet } from '../../../../../utils/constants';
import { useWindowSize } from '../../../../../utils/hooks';

const EditView = ({
  item,
  renderIndustries,
  renderTypes,
  renderRemoteWorkStatus,
  classes,
  isEditViewVisible,
  onEditViewChange,
  cardStyleFade
}) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const size = useWindowSize();
  const [isActive, setActive] = useState(item.IsActive);
  const [isFrequency, setFrequency] = useState(item.Frequency ? item.Frequency : t('dashboard.settings.weekly'));
  const [openPopUp, setOpenPop] = useState(false);
  const deletePending = useSelector((state) => state.jobAlerts.pendingDelete);
  const allAlerts = useSelector((state) => state.jobAlerts.alerts);

  const handleActiveToggle = () => {
    setActive(!isActive);
  };

  const finalDeleteHandler = async () => {
    const deletedAlert = allAlerts.find((alert) => alert.Id === item.Id);
    try {
      await dispatch(deleteJobAlert(item.Id));
      dispatch(
        setNotification({
          isAppStyled: true,
          message: `1 ${t('common.general.deleted')}`,
          duration: 3000,
          type: appNotificationsTypes.SUCCESS,
          action: (
            <p
              role="presentation"
              className={classes.undoBtn}
              onClick={() => {
                dispatch(revertDeletedJobAlert(deletedAlert));
                dispatch(removeNotification());
                onEditViewChange(!isEditViewVisible);
              }}
            >
              {t('common.general.undo')}
            </p>
          )
        })
      );
      dispatch(updateCardStyle(!cardStyleFade));
    } catch (err) {
      dispatch(
        setNotification({
          isAppStyled: true,
          message: `${err.message}`,
          duration: 3000,
          type: appNotificationsTypes.SUCCESS
        })
      );
    }
  };

  const popUpModals = {
    delete: (
      <DeletePopUp
        deleteHandler={finalDeleteHandler}
        closeHandler={() => {
          setOpenPop(false);
        }}
        pending={deletePending}
        withBackbtn={size.width < responsiveSizeTimesheet}
      />
    )
  };

  const deleteHandler = () => {
    setOpenPop('delete');
  };

  const saveHandler = async (e) => {
    try {
      const data = {
        ...item,
        IsActive: isActive,
        Frequency: isFrequency
      };
      await dispatch(editJobAlert(data));
      dispatch(
        setNotification({
          isAppStyled: true,
          message: `${t('dashboard.settings.savetext')}`,
          duration: 3000,
          type: appNotificationsTypes.SUCCESS
        })
      );
      onEditViewChange(!isEditViewVisible);
      dispatch(updateCardStyle(!cardStyleFade));
    } catch (err) {
      dispatch(
        setNotification({
          isAppStyled: true,
          message: `${err.message}`,
          duration: 3000,
          type: appNotificationsTypes.SUCCESS
        })
      );
    }
  };

  return (
    <div className={classes.detailText}>
      <div className={classes.filters}>
        {t('dashboard.settings.filters')}
        {item.JobAlertIndustries && renderIndustries(item.JobAlertIndustries)}
        {item.JobAlertIndustries?.length && item.JobAlertContractTypes?.length ? (
          <span> &bull;&nbsp;{renderTypes(item.JobAlertContractTypes)}</span>
        ) : (
          <span> {renderTypes(item.JobAlertContractTypes)}</span>
        )}
        {(item.JobAlertIndustries?.length || item.JobAlertContractTypes?.length) && item.RemoteWork ? (
          <span> &bull;&nbsp;{renderRemoteWorkStatus(item.RemoteWork)}</span>
        ) : (
          <span> {renderRemoteWorkStatus(item.RemoteWork)}</span>
        )}
      </div>
      <div className={classes.detailHeader}>
        <div className={classes.emailText}>
          <span>{t('dashboard.settings.email')}</span>
          <MailboxIcon />
        </div>
        <ToggleSwitch checked={isActive} onChange={handleActiveToggle} />
      </div>
      <div className={classes.frequency}>
        <RadioButton
          label={t('dashboard.settings.daily').charAt(0).toUpperCase() + t('dashboard.settings.daily').slice(1)}
          checked={isFrequency === t('dashboard.settings.daily')}
          disabled={!isActive}
          onChange={() => setFrequency(t('dashboard.settings.daily'))}
        />
        <RadioButton
          label={t('dashboard.settings.weekly').charAt(0).toUpperCase() + t('dashboard.settings.weekly').slice(1)}
          checked={isFrequency === t('dashboard.settings.weekly')}
          disabled={!isActive}
          onChange={() => setFrequency(t('dashboard.settings.weekly'))}
        />
      </div>
      <div className={classes.actions}>
        <MainButton rounded forceWidth="100%" action={saveHandler}>
          <span>{t('dashboard.settings.save')}</span>
        </MainButton>
        <div role="presentation" onClick={deleteHandler}>
          <TextButton>{t('dashboard.settings.delete')}</TextButton>
        </div>
      </div>
      <BaseModal
        open={openPopUp}
        fullscreen={false}
        closeHandler={() => setOpenPop('')}
        title="&nbsp;"
        hideFooter
        hideHeader
        withSeparator={false}
        marginLeftRight={1}
      >
        <WithCloseIcon close={() => setOpenPop('')} y={6}>
          {popUpModals[openPopUp]}
        </WithCloseIcon>
      </BaseModal>
    </div>
  );
};

export default EditView;
